// src/pages/widgets/ReUploadWidget.js

import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/api';
import VerificationModal from './VerificationModal'; 
import './style/ReUploadWidget.css';

const ReUploadWidget = ({ 
  documentName, 
  currentCategory, 
  currentStartDate, 
  currentExpireDate, 
  currentAlertDays, 
  currentAlertFrequency, 
  currentOrganisation, 
  currentOrganisationId, 
  onClose, 
  onUpload 
}) => {
  
  const parseDateForInput = (dateString) => {
    if (!dateString) return '';
    if (typeof dateString === 'string' && dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    if (typeof dateString === 'string' && dateString.length === 10) {
      return dateString;
    }
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return '';
  };

  const getInitialAlertTiming = (val) => {
    if (!val) return '';
    const numVal = parseInt(val, 10);
    if (numVal === 30) return '1 Month Before';
    if (numVal === 60) return '2 Months Before';
    if (numVal === 90) return '3 Months Before';
    if (numVal === 120) return '4 Months Before';
    if (numVal === 150) return '5 Months Before';
    return '';
  };

  const [file, setFile] = useState(null);
  const [name, setName] = useState(documentName || '');
  const [category, setCategory] = useState(''); 
  const [categoryList, setCategoryList] = useState([]); 
  const [startDate, setStartDate] = useState(parseDateForInput(currentStartDate));
  const [expireDate, setExpireDate] = useState(parseDateForInput(currentExpireDate));
  
  // --- Alert Switch State ---
  const [enableAlerts, setEnableAlerts] = useState(currentAlertDays > 0);
  const [alertTiming, setAlertTiming] = useState(getInitialAlertTiming(currentAlertDays) || '1 Month Before');
  const [alertFrequency, setAlertFrequency] = useState(currentAlertFrequency || 'Weekly');

  // --- Archive Checkbox State ---
  const [archiveOld, setArchiveOld] = useState(false);

  const [organisations, setOrganisations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(currentOrganisationId || '');
  const [searchTerm, setSearchTerm] = useState(currentOrganisation || ''); 
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const [showVerificationModal, setShowVerificationModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const orgData = await apiService.getOrganisations();
        setOrganisations(orgData);
        
        if (currentOrganisationId && !currentOrganisation) {
            const match = orgData.find(o => o.id === currentOrganisationId);
            if (match) {
                setSearchTerm(match.name);
            }
        }

        const catData = await apiService.getCategoryList();
        const list = Array.isArray(catData) ? catData : [];
        setCategoryList(list);

        if (currentCategory && list.length > 0) {
          const foundCatByName = list.find(c => c.name === currentCategory);
          const foundCatById = list.find(c => c.id == currentCategory);

          if (foundCatByName) {
            setCategory(foundCatByName.id);
          } else if (foundCatById) {
            setCategory(foundCatById.id);
          } else {
            setCategory(currentCategory);
          }
        }
      } catch (error) {
        console.error("Failed to load data", error);
        setCategoryList([]); 
      }
    };
    fetchData();
  }, [currentCategory, currentOrganisationId, currentOrganisation]); 

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOrgs = organisations.filter(org => {
    const orgName = org.name ? String(org.name).toLowerCase() : '';
    const search = searchTerm ? String(searchTerm).toLowerCase() : '';
    return orgName.includes(search);
  });

  const handleOrgSelect = (org) => {
    setSelectedOrgId(org.id);
    setSearchTerm(org.name); 
    setShowDropdown(false);
  };

  const handleSubmit = async () => {
    let daysToSend = 0; 
    
    // --- LOGIC: Only calculate days if Switch is ON ---
    if (enableAlerts) {
      if (alertTiming === '1 Month Before') daysToSend = 30;
      else if (alertTiming === '2 Months Before') daysToSend = 60;
      else if (alertTiming === '3 Months Before') daysToSend = 90;
      else if (alertTiming === '4 Months Before') daysToSend = 120;
      else if (alertTiming === '5 Months Before') daysToSend = 150;
    } else {
      daysToSend = 0; // Explicitly 0 to turn off alerts in DB
    }

    try {
      const selectedOrgObj = organisations.find(org => org.id === selectedOrgId);
      const orgNameToSend = selectedOrgObj ? selectedOrgObj.name : 'General';

      const selectedCatObj = categoryList.find(cat => cat.id == category);
      const catNameToSend = selectedCatObj ? selectedCatObj.name : 'Uncategorized';

      console.log(`Submitting. Archive: ${archiveOld}, Alerts Enabled: ${enableAlerts}, Days: ${daysToSend}`);

      await onUpload(
        file, 
        startDate, 
        expireDate, 
        selectedOrgId, 
        daysToSend, 
        name, 
        category,
        orgNameToSend, 
        catNameToSend,
        alertFrequency,
        archiveOld 
      );

    } catch (error) {
      const isForbidden = 
        (error.response && error.response.status === 403) ||
        (error.message && (error.message.includes('Access Denied') || error.message.includes('Forbidden'))) ||
        (error.response && error.response.data && error.response.data.code === 'KEY_VERIFICATION_REQUIRED');

      if (isForbidden) {
        setShowVerificationModal(true);
      } else {
        console.error("Upload failed", error);
        alert("Update failed: " + (error.response?.data?.message || error.message));
      }
    }
  };

  return (
    <>
      {showVerificationModal && (
        <VerificationModal 
          isOpen={showVerificationModal} 
          onClose={() => setShowVerificationModal(false)} 
        />
      )}

      <div className="reupload-widget__overlay">
        <div className="reupload-widget__container">
          
          <div className="reupload-widget__header">
            <h3 className="reupload-widget__title">Edit / Re-upload Document</h3>
            <button className="reupload-widget__close-btn" onClick={onClose}>&times;</button>
          </div>

          <div className="reupload-widget__body">
            
            {/* --- FILE & ARCHIVE SECTION --- */}
            <div className="reupload-widget__group">
              <label className="reupload-widget__label">Replace File (Optional)</label>
              <input 
                className="reupload-widget__input-file"
                type="file" 
                onChange={(e) => {
                  const selectedFile = e.target.files[0];
                  setFile(selectedFile);
                  // Removed logic that auto-unchecked archiveOld
                }} 
              />
              <small className="reupload-widget__hint">Leave empty to keep existing file.</small>
              
              {/* Archive Switch - ALWAYS ENABLED */}
              <div style={{ 
                marginTop: '12px', 
                display: 'flex', 
                alignItems: 'center', 
                backgroundColor: '#e6f7ff', // Always blue tint
                padding: '10px', 
                borderRadius: '4px', 
                border: '1px solid #1890ff',
                opacity: 1,
                transition: 'all 0.3s ease'
              }}>
                <input 
                  type="checkbox" 
                  id="archiveOld" 
                  checked={archiveOld} 
                  onChange={(e) => setArchiveOld(e.target.checked)}
                  // Removed disabled={!file}
                  style={{ marginRight: '10px', width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <label 
                  htmlFor="archiveOld" 
                  style={{ 
                    fontSize: '0.9rem', 
                    cursor: 'pointer', 
                    userSelect: 'none', 
                    color: '#333', 
                    fontWeight: '500',
                    margin: 0
                  }}
                >
                  Archive current version?
                </label>
              </div>
              <small style={{ color: '#666', marginLeft: '4px', fontSize: '0.8rem', marginTop: '5px', display: 'block' }}>
                (If checked, a snapshot of the current file will be saved to the archive.)
              </small>
            </div>

            <div className="reupload-widget__group">
              <label className="reupload-widget__label">Document Name</label>
              <input 
                className="reupload-widget__input"
                type="text" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
              />
            </div>

            <div className="reupload-widget__group">
              <label className="reupload-widget__label">Category</label>
              <select 
                className="reupload-widget__select"
                value={category} 
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">Select Category...</option>
                {categoryList.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="reupload-widget__group" ref={dropdownRef}>
              <label className="reupload-widget__label">Organisation</label>
              <input 
                className="reupload-widget__input"
                type="text" 
                placeholder="Search Organisation..." 
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowDropdown(true);
                  if (selectedOrgId) setSelectedOrgId(''); 
                }}
                onFocus={() => setShowDropdown(true)}
              />
              {showDropdown && (
                <ul className="reupload-widget__dropdown">
                  {filteredOrgs.length > 0 ? (
                    filteredOrgs.map(org => (
                      <li 
                        key={org.id} 
                        className="reupload-widget__dropdown-item"
                        onClick={() => handleOrgSelect(org)}
                      >
                        {org.name}
                      </li>
                    ))
                  ) : (
                    <li className="reupload-widget__dropdown-empty">No organisations found</li>
                  )}
                </ul>
              )}
            </div>

            <div className="reupload-widget__row">
              <div className="reupload-widget__group reupload-widget__group--half">
                <label className="reupload-widget__label">Start Date</label>
                <input 
                  className="reupload-widget__input"
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)} 
                />
              </div>
              <div className="reupload-widget__group reupload-widget__group--half">
                <label className="reupload-widget__label">Expire Date</label>
                <input 
                  className="reupload-widget__input"
                  type="date" 
                  value={expireDate} 
                  onChange={(e) => setExpireDate(e.target.value)} 
                />
              </div>
            </div>

            {/* --- ALERT SECTION --- */}
            <div className="reupload-widget__group" style={{ backgroundColor: '#f9fafb', padding: '10px', borderRadius: '6px', border: '1px solid #eee' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: enableAlerts ? '10px' : '0' }}>
                <label className="reupload-widget__label" style={{ margin: 0 }}>Enable Expiry Alerts</label>
                
                {/* Alert Toggle Switch */}
                <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '40px', height: '20px' }}>
                  <input 
                    type="checkbox" 
                    checked={enableAlerts}
                    onChange={(e) => setEnableAlerts(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: enableAlerts ? '#1890ff' : '#ccc',
                    transition: '.4s', borderRadius: '34px'
                  }}>
                    <span style={{
                      position: 'absolute', content: '""', height: '16px', width: '16px',
                      left: '2px', bottom: '2px', backgroundColor: 'white',
                      transition: '.4s', borderRadius: '50%',
                      transform: enableAlerts ? 'translateX(20px)' : 'translateX(0)'
                    }}></span>
                  </span>
                </label>
              </div>

              {/* Only show dropdowns if Alerts are Enabled */}
              {enableAlerts && (
                <div style={{ animation: 'fadeIn 0.3s' }}>
                  <label className="reupload-widget__label" style={{ fontSize: '0.85rem', color: '#666' }}>Alert Timing</label>
                  <select 
                    className="reupload-widget__select"
                    value={alertTiming} 
                    onChange={(e) => setAlertTiming(e.target.value)}
                  >
                    <option value="1 Month Before">1 Month Before</option>
                    <option value="2 Months Before">2 Months Before</option>
                    <option value="3 Months Before">3 Months Before</option>
                    <option value="4 Months Before">4 Months Before</option>
                    <option value="5 Months Before">5 Months Before</option>
                  </select>

                  <div style={{ marginTop: '10px' }}>
                    <label className="reupload-widget__label" style={{ fontSize: '0.85rem', color: '#666' }}>Repeat Frequency</label>
                    <select 
                      className="reupload-widget__select"
                      value={alertFrequency} 
                      onChange={(e) => setAlertFrequency(e.target.value)}
                    >
                      <option value="Weekly">Weekly</option>
                      <option value="Monthly">Monthly</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

          </div>

          <div className="reupload-widget__footer">
            <button className="reupload-widget__btn-cancel" onClick={onClose}>Cancel</button>
            <button className="reupload-widget__btn-save" onClick={handleSubmit}>Save Changes</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReUploadWidget;
